import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, SESSION_REVOKED_STORAGE_KEY, type SessionRevokedNotice } from "@/lib/queryClient";
import { logoutOneSignalIdentity } from "@/hooks/use-onesignal-identity";
import { clearReturnTo } from "@/lib/auth-return-to";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LogOut, UserX, Mail, LogIn } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { usePageTitle } from "@/hooks/use-page-title";
import { resetAppAnalytics } from "@/lib/app-analytics";

function readRevokedNotice(): SessionRevokedNotice | null {
  try {
    const raw = sessionStorage.getItem(SESSION_REVOKED_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionRevokedNotice;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearRevokedNotice() {
  try {
    sessionStorage.removeItem(SESSION_REVOKED_STORAGE_KEY);
  } catch {}
}

export default function AppNoTeam() {
  const queryClient = useQueryClient();
  usePageTitle("No Team");
  const [, navigate] = useLocation();
  const [revokedNotice, setRevokedNotice] = useState<SessionRevokedNotice | null>(() => readRevokedNotice());

  const userQuery = useQuery<{
    user: { id: number; email: string; name: string | null; status?: string; selectedPlan?: string };
    activeMembership?: { ownerName: string; ownerEmail: string } | null;
  }>({
    queryKey: ["/api/app/auth/me"],
    queryFn: async () => {
      const res = await fetch("/api/app/auth/me", { credentials: "include" });
      if (!res.ok) throw new Error("Not authenticated");
      return res.json();
    },
    retry: false,
  });

  const logoutMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      await apiRequest("POST", "/api/app/auth/logout", {});
      void resetAppAnalytics();
    },
    onSuccess: () => {
      logoutOneSignalIdentity();
      clearReturnTo();
      clearRevokedNotice();
      queryClient.clear();
      navigate("/app/login");
    },
  });

  useEffect(() => {
    if (revokedNotice) return;
    if (userQuery.isError) {
      navigate("/app/login");
      return;
    }
    if (userQuery.data) {
      const { user, activeMembership } = userQuery.data;
      const ownPlan = user.selectedPlan || 'Host';
      const isInterest = ownPlan === 'Interest' || user.status === 'waitlist';
      if (activeMembership || !isInterest) {
        navigate("/app/dashboard");
      }
    }
  }, [userQuery.data, userQuery.isError, navigate, revokedNotice]);

  const user = userQuery.data?.user;

  if (revokedNotice) {
    const teamLabel = revokedNotice.ownerName ? `${revokedNotice.ownerName}'s team` : "your team";
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <Link href="/">
          <img src={logoImg} alt="PadPulse" className="h-10 w-auto mb-8 cursor-pointer" data-testid="link-logo" />
        </Link>
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 pb-6 px-6 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <UserX className="h-6 w-6 text-amber-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-testid="text-removed-title">
              You were removed from {teamLabel}
            </h1>
            <p className="text-sm text-gray-600 mb-2" data-testid="text-removed-description">
              {revokedNotice.ownerName
                ? `${revokedNotice.ownerName} removed your access to PadPulse.`
                : "Your team access to PadPulse was removed."}
            </p>
            <p className="text-sm text-gray-600 mb-6">
              You can log in again to start your own PadPulse account, or reach out to the team owner if this was a mistake.
            </p>

            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={() => {
                  clearRevokedNotice();
                  setRevokedNotice(null);
                  queryClient.clear();
                  navigate("/app/login");
                }}
                data-testid="button-removed-login"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Log in to start your own account
              </Button>
              {revokedNotice.ownerEmail && (
                <a
                  href={`mailto:${revokedNotice.ownerEmail}?subject=PadPulse%20team%20access`}
                  className="block"
                >
                  <Button variant="outline" className="w-full" data-testid="button-removed-contact-owner">
                    <Mail className="h-4 w-4 mr-2" />
                    Contact {revokedNotice.ownerName || "team owner"}
                  </Button>
                </a>
              )}
              <a
                href="mailto:hello@padpulse.io?subject=PadPulse%20account%20access"
                className="block"
              >
                <Button variant="ghost" className="w-full text-gray-600" data-testid="button-removed-contact-support">
                  Contact PadPulse Support
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <Link href="/">
        <img src={logoImg} alt="PadPulse" className="h-10 w-auto mb-8 cursor-pointer" data-testid="link-logo" />
      </Link>
      <Card className="max-w-lg w-full">
        <CardContent className="pt-8 pb-6 px-6 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <UserX className="h-6 w-6 text-gray-500" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2" data-testid="text-no-team-title">
            You're not on a team right now
          </h1>
          <p className="text-sm text-gray-600 mb-6" data-testid="text-no-team-description">
            Your team membership is no longer active. PadPulse access requires either an active subscription or membership on someone else's team.
          </p>

          {user?.email && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-6 text-left">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Signed in as</p>
              <p className="text-sm font-medium text-gray-900" data-testid="text-current-email">{user.email}</p>
            </div>
          )}

          <div className="space-y-3">
            <a
              href="mailto:hello@padpulse.io?subject=PadPulse%20account%20access"
              className="block"
            >
              <Button variant="outline" className="w-full" data-testid="button-contact-support">
                <Mail className="h-4 w-4 mr-2" />
                Contact Support
              </Button>
            </a>
            <Button
              variant="ghost"
              className="w-full text-gray-600"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
              data-testid="button-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-6">
            If you were recently invited to a team, ask the team owner to re-send the invite to <strong>{user?.email || "your email"}</strong>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
